import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  Barcode,
  Check,
  Copy as CopyIcon,
  Filter,
  History,
  Images,
  Loader2,
  Repeat,
  Sparkles,
  Store,
  Wand2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useClientConfig } from '@/components/client-logo';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { features } from '@/config/features';
import {
  activePrice,
  toItemInput,
  useAssignToAllStores,
  useAssignToStore,
  useAssignedStores,
  useArchiveItem,
  useCreateItem,
  useDivisions,
  useCopyStoreConfig,
  useForcePrice,
  useRestoreItem,
  useUpdateItem,
  type AssignedStore,
  type MasterItem,
  type MasterItemInput,
} from './item-master.api';
import { ListSkeleton, itemLabel } from './item-drawers';

/**
 * One catalogue item.
 *
 * Two tabs: what the product *is*, and which stores stock it. The lists it owns
 * — barcodes, pictures, filters, recommendations, substitutes, history — open
 * from here as their own drawers, because each is a job of its own and none of
 * them fits beside a form.
 */

const NEW_ITEM: MasterItemInput = {
  gtin: '',
  brand: '',
  name: '',
  description: '',
  nameSpa: '',
  descriptionSpa: '',
  itemImage: '',
  itemThumbnail: '',
  searchTags: '',
  isDeleted: false,
  isPackagingSensitive: false,
  isTemperatureSensitive: false,
  isAlcoholicBeverage: false,
  isPerishable: false,
  isEbt: false,
  idRequired: false,
  affectsInventory: false,
  internalId: '',
  suggestedDepartment: '',
  internalRank: 0,
  hasPluCode: false,
  businessIds: [],
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <Label className="text-sm font-normal">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function ItemEditorSheet({
  item,
  open,
  onOpenChange,
  onOpenList,
  onOpenStore,
  onCreated,
}: {
  /** `null` closes it; an item edits; `'new'` creates. */
  item: MasterItem | 'new' | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenList: (
    list:
      | 'gtins'
      | 'media'
      | 'filters'
      | 'recommendation'
      | 'substitute'
      | 'logs'
      | 'metadata',
    item: MasterItem
  ) => void;
  /** Open one store's offer. The rest ride along for "apply to multiple". */
  onOpenStore: (store: AssignedStore, others: AssignedStore[]) => void;
  onCreated: (item: MasterItem) => void;
}) {
  const { t } = useTranslation();

  const existing = item !== 'new' ? item : null;
  const isEdit = !!existing;

  const update = useUpdateItem();
  const create = useCreateItem();
  const archive = useArchiveItem();
  const restore = useRestoreItem();
  const forcePrice = useForcePrice();
  const copyConfig = useCopyStoreConfig();
  const divisions = useDivisions();
  const { data: businesses } = useBusinesses();
  const { data: clientConfig } = useClientConfig();

  const stores = useAssignedStores(open && existing ? existing.id : null);
  const assignAll = useAssignToAllStores();
  const assignOne = useAssignToStore();

  const [draft, setDraft] = useState<MasterItemInput | null>(null);
  const [hydrated, setHydrated] = useState<string | null>(null);
  const [assignTo, setAssignTo] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [copyTo, setCopyTo] = useState<string[]>([]);

  const key = existing?.id ?? (item === 'new' ? 'new' : null);
  if (item && hydrated !== key) {
    setHydrated(key);
    setDraft(existing ? toItemInput(existing) : { ...NEW_ITEM });
    setAssignTo('');
    setCopyFrom('');
    setCopyTo([]);
  }

  if (!draft || !item) return null;

  const set = (patch: Partial<MasterItemInput>) =>
    setDraft((current) => (current ? { ...current, ...patch } : current));

  const failed = () => toast.error(t('itemMaster.actionFailed'));
  const saving = update.isPending || create.isPending;

  /** Stores that do not stock this item yet. */
  const assigned = new Set((stores.data ?? []).map((s) => s.businessId));
  const available = (businesses ?? []).filter((b) => !assigned.has(b.id));

  // Copying a configuration only means something once a second store exists to
  // copy it to. Several prices per item used to rule the whole feature out;
  // the update takes a `prices` array now, so they travel like anything else.
  const stocking = stores.data ?? [];
  const canCopy = stocking.length > 1;
  const sourceStore = stocking.find((store) => store.id === copyFrom) ?? null;
  const targets = stocking.filter((store) => store.id !== copyFrom);
  const chosen = copyTo.filter((id) => id !== copyFrom);

  function save() {
    if (isEdit) {
      update.mutate(
        { id: existing!.id, input: draft! },
        {
          onSuccess: () => {
            toast.success(t('itemMaster.saved'));
            onOpenChange(false);
          },
          onError: failed,
        }
      );
    } else {
      create.mutate(draft!, {
        onSuccess: (saved) => {
          toast.success(t('itemMaster.created'));
          // The new row carries its id, so its barcodes and pictures can be
          // added next without hunting for it in the list.
          onCreated(saved);
        },
        onError: failed,
      });
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="flex-row flex-wrap items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit ? t('itemMaster.editItem') : t('itemMaster.newItem')}
          </SheetTitle>
          {isEdit &&
            (existing!.isDeleted ? (
              <Button
                variant="outline"
                size="sm"
                disabled={restore.isPending}
                onClick={() =>
                  restore.mutate(existing!.id, {
                    onSuccess: () => toast.success(t('itemMaster.restored')),
                    onError: failed,
                  })
                }
              >
                <ArchiveRestore className="size-3.5" />
                {t('itemMaster.restore')}
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={archive.isPending}
                  >
                    <Archive className="size-3.5" />
                    {t('itemMaster.archive')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('itemMaster.archive')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('itemMaster.archivePrompt')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        archive.mutate(existing!.id, {
                          onSuccess: () => {
                            toast.success(t('itemMaster.archived'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ))}
        </SheetHeader>

        <div className="p-4">
          {/* The item's own lists. Offered only once it has an id — before
              that there is nothing for a barcode to belong to. */}
          {isEdit && (
            <div className="mb-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('gtins', existing!)}
              >
                <Barcode className="size-3.5" />
                {t('itemMaster.gtins')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('media', existing!)}
              >
                <Images className="size-3.5" />
                {t('itemMaster.media')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('filters', existing!)}
              >
                <Filter className="size-3.5" />
                {t('itemMaster.filters')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('recommendation', existing!)}
              >
                <Sparkles className="size-3.5" />
                {t('itemMaster.recommendations')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('substitute', existing!)}
              >
                <Repeat className="size-3.5" />
                {t('itemMaster.substitutes')}
              </Button>
              {features.images && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenList('metadata', existing!)}
                >
                  <Wand2 className="size-3.5" />
                  {t('itemMaster.productMetadata')}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenList('logs', existing!)}
              >
                <History className="size-3.5" />
                {t('itemMaster.logs')}
              </Button>
            </div>
          )}

          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">
                {t('itemMaster.details')}
              </TabsTrigger>
              <TabsTrigger value="stores" disabled={!isEdit}>
                {t('itemMaster.stores')}
                {isEdit && (stores.data ?? []).length > 0 && (
                  <span className="ml-1 tabular-nums">
                    ({stores.data!.length})
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ------------------------------------------- details */}
            <TabsContent value="details" className="space-y-4">
              {isEdit && (
                <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span>
                    {t('itemMaster.primaryGtin')}:{' '}
                    <span className="font-mono">{existing!.gtin || '—'}</span>
                  </span>
                  <span>
                    {t('itemMaster.createdOn')}:{' '}
                    {formatDateTime(existing!.createdAt)}
                  </span>
                  {existing!.isDeleted && (
                    <Badge variant="destructive">
                      {t('itemMaster.archived')}
                    </Badge>
                  )}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="GTIN" hint={isEdit ? t('itemMaster.gtinHint') : undefined}>
                  <Input
                    className="font-mono"
                    value={draft.gtin ?? ''}
                    onChange={(e) => set({ gtin: e.target.value })}
                  />
                </Field>
                <Field label={t('itemMaster.internalId')}>
                  <Input
                    value={draft.internalId ?? ''}
                    onChange={(e) => set({ internalId: e.target.value })}
                  />
                </Field>
              </div>

              <Field label={t('itemMaster.brand')}>
                <Input
                  value={draft.brand ?? ''}
                  onChange={(e) => set({ brand: e.target.value })}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={`${t('itemMaster.name')} (EN)`}>
                  <Input
                    value={draft.name ?? ''}
                    onChange={(e) => set({ name: e.target.value })}
                  />
                </Field>
                <Field label={`${t('itemMaster.name')} (ES)`}>
                  <Input
                    value={draft.nameSpa ?? ''}
                    onChange={(e) => set({ nameSpa: e.target.value })}
                  />
                </Field>
                <Field label={`${t('itemMaster.description')} (EN)`}>
                  <Input
                    value={draft.description ?? ''}
                    onChange={(e) => set({ description: e.target.value })}
                  />
                </Field>
                <Field label={`${t('itemMaster.description')} (ES)`}>
                  <Input
                    value={draft.descriptionSpa ?? ''}
                    onChange={(e) => set({ descriptionSpa: e.target.value })}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('itemMaster.division')}>
                  {/* Free text with the known divisions behind it: the API
                      takes a string, and a typo would coin a new one. */}
                  <Input
                    list="catalog-divisions"
                    value={draft.suggestedDepartment ?? ''}
                    onChange={(e) =>
                      set({ suggestedDepartment: e.target.value })
                    }
                  />
                  <datalist id="catalog-divisions">
                    {(divisions.data ?? []).map((division) => (
                      <option key={division} value={division} />
                    ))}
                  </datalist>
                </Field>
                <Field label={t('itemMaster.rank')} hint={t('itemMaster.rankHint')}>
                  <Input
                    type="number"
                    value={draft.internalRank ?? 0}
                    onChange={(e) =>
                      set({ internalRank: Number(e.target.value) })
                    }
                  />
                </Field>
              </div>

              <Field
                label={t('itemMaster.searchTags')}
                hint={t('itemMaster.searchTagsHint')}
              >
                <Textarea
                  rows={2}
                  value={draft.searchTags ?? ''}
                  onChange={(e) => set({ searchTags: e.target.value })}
                />
              </Field>

              <div className="grid gap-x-4 sm:grid-cols-2">
                <Toggle
                  label={t('itemMaster.packagingSensitive')}
                  checked={draft.isPackagingSensitive}
                  onChange={(isPackagingSensitive) =>
                    set({ isPackagingSensitive })
                  }
                />
                <Toggle
                  label={t('itemMaster.temperatureSensitive')}
                  checked={draft.isTemperatureSensitive}
                  onChange={(isTemperatureSensitive) =>
                    set({ isTemperatureSensitive })
                  }
                />
                <Toggle
                  label={t('itemMaster.alcoholicBeverage')}
                  checked={draft.isAlcoholicBeverage}
                  onChange={(isAlcoholicBeverage) => set({ isAlcoholicBeverage })}
                />
                <Toggle
                  label={t('itemMaster.perishable')}
                  checked={draft.isPerishable}
                  onChange={(isPerishable) => set({ isPerishable })}
                />
                <Toggle
                  label={t('itemMaster.isEbt')}
                  checked={draft.isEbt}
                  onChange={(isEbt) => set({ isEbt })}
                />
                <Toggle
                  label={t('itemMaster.idRequired')}
                  checked={draft.idRequired}
                  onChange={(idRequired) => set({ idRequired })}
                />
                <Toggle
                  label={t('itemMaster.hasPluCode')}
                  checked={draft.hasPluCode}
                  onChange={(hasPluCode) => set({ hasPluCode })}
                />
                <Toggle
                  label={t('itemMaster.affectsInventory')}
                  checked={draft.affectsInventory}
                  onChange={(affectsInventory) => set({ affectsInventory })}
                />
              </div>
            </TabsContent>

            {/* -------------------------------------------- stores */}
            <TabsContent value="stores" className="space-y-3">
              {isEdit && (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Select value={assignTo} onValueChange={setAssignTo}>
                      <SelectTrigger className="flex-1">
                        <SelectValue
                          placeholder={t('itemMaster.pickAStore')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {available.map((business) => (
                          <SelectItem key={business.id} value={business.id}>
                            {business.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      disabled={!assignTo || assignOne.isPending}
                      onClick={() =>
                        assignOne.mutate(
                          { itemId: existing!.id, businessId: assignTo },
                          {
                            onSuccess: () => {
                              toast.success(t('itemMaster.assigned'));
                              setAssignTo('');
                            },
                            onError: failed,
                          }
                        )
                      }
                    >
                      <Store className="size-3.5" />
                      {t('itemMaster.assignToStore')}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={
                        available.length === 0 || assignAll.isPending
                      }
                      onClick={() =>
                        assignAll.mutate(
                          {
                            itemId: existing!.id,
                            businessIds: available.map((b) => b.id),
                          },
                          {
                            onSuccess: () =>
                              toast.success(t('itemMaster.assigned')),
                            onError: failed,
                          }
                        )
                      }
                    >
                      {t('itemMaster.assignToAll')}
                    </Button>
                  </div>

                  {/* Copying is meaningless with one store, so the whole
                      block only appears once there are two. */}
                  {canCopy && (
                    <div className="bg-muted/40 space-y-3 rounded-xl border p-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <CopyIcon className="text-muted-foreground size-4" />
                          <p className="text-sm font-medium">
                            {t('itemMaster.copyConfig')}
                          </p>
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {t('itemMaster.copyConfigHint')}
                        </p>
                      </div>

                      <div className="space-y-1.5">
                        <Label>{t('itemMaster.copyFrom')}</Label>
                        <Select
                          value={copyFrom}
                          onValueChange={(value) => {
                            setCopyFrom(value);
                            setCopyTo([]);
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t('itemMaster.pickSourceStore')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {stocking.map((store) => (
                              <SelectItem key={store.id} value={store.id}>
                                {store.businessName}
                                {' — '}
                                {formatCurrency(activePrice(store))}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {sourceStore && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <Label>{t('itemMaster.copyTo')}</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7"
                              onClick={() =>
                                setCopyTo(
                                  chosen.length === targets.length
                                    ? []
                                    : targets.map((store) => store.id)
                                )
                              }
                            >
                              {chosen.length === targets.length
                                ? t('itemMaster.clearAll')
                                : t('itemMaster.selectAll')}
                            </Button>
                          </div>
                          {/* Chips rather than checkboxes: they stay usable
                              with a thumb, and wrap on a narrow screen. */}
                          <div className="flex flex-wrap gap-1.5">
                            {targets.map((store) => {
                              const picked = chosen.includes(store.id);
                              return (
                                <button
                                  key={store.id}
                                  type="button"
                                  onClick={() =>
                                    setCopyTo((current) =>
                                      picked
                                        ? current.filter(
                                            (id) => id !== store.id
                                          )
                                        : [...current, store.id]
                                    )
                                  }
                                  className={cn(
                                    'flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
                                    picked
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'bg-background hover:bg-accent'
                                  )}
                                >
                                  {picked && <Check className="size-3" />}
                                  {store.businessName}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="bg-background w-full"
                            disabled={
                              !sourceStore
                              || chosen.length === 0
                              || copyConfig.isPending
                            }
                          >
                            {copyConfig.isPending && (
                              <Loader2 className="animate-spin" />
                            )}
                            {t('itemMaster.copyNow')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t('itemMaster.copyConfig')}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('itemMaster.copyConfigPrompt', {
                                store: sourceStore?.businessName ?? '',
                                count: chosen.length,
                              })}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>
                              {t('common.cancel')}
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                copyConfig.mutate(
                                  {
                                    source: sourceStore!,
                                    targetIds: chosen,
                                  },
                                  {
                                    onSuccess: () => {
                                      toast.success(
                                        t('itemMaster.configCopied')
                                      );
                                      setCopyTo([]);
                                    },
                                    onError: failed,
                                  }
                                )
                              }
                            >
                              {t('itemMaster.copyNow')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}

                  {/* A different thing entirely: re-pull every store's price
                      from the client's price integration. There is no source
                      to pick — the integration is the source — and a client
                      without one cannot do it at all, which is how the old
                      drawer gated the same button. */}
                  {stocking.length > 1 && clientConfig?.hasPriceIntegration && (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={forcePrice.isPending}
                      onClick={() =>
                        forcePrice.mutate(existing!.id, {
                          onSuccess: () =>
                            toast.success(t('itemMaster.pricesRefreshed')),
                          onError: failed,
                        })
                      }
                    >
                      {forcePrice.isPending && (
                        <Loader2 className="animate-spin" />
                      )}
                      {t('itemMaster.forcePriceUpdate')}
                    </Button>
                  )}

                  {stores.isLoading && <ListSkeleton rows={3} />}

                  {stores.isSuccess && (stores.data ?? []).length === 0 && (
                    <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
                      {t('itemMaster.noStores')}
                    </p>
                  )}

                  {(stores.data ?? []).length > 0 && (
                    <div className="relative rounded-xl">
                      <BusyOverlay show={stores.isFetching} />
                      <div
                        className={cn(
                          'divide-y rounded-xl border',
                          stores.isFetching && 'opacity-60 transition-opacity'
                        )}
                      >
                        {stores.data!.map((store) => (
                          <button
                            key={store.id}
                            type="button"
                            className="hover:bg-accent w-full space-y-1 p-3 text-left transition-colors"
                            onClick={() =>
                              onOpenStore(
                                store,
                                stocking.filter((row) => row.id !== store.id)
                              )
                            }
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium">
                                  {store.businessName}
                                </p>
                                <p className="text-muted-foreground truncate font-mono text-xs">
                                  {store.upc}
                                </p>
                              </div>
                              {/* The live price comes from the row's price
                                  list, not from a field on the row. */}
                              <p className="shrink-0 font-semibold tabular-nums">
                                {formatCurrency(activePrice(store))}
                              </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-1.5">
                              {store.isDeleted && (
                                <Badge variant="secondary">
                                  {t('itemMaster.archived')}
                                </Badge>
                              )}
                              <Badge
                                variant={store.isVisible ? 'success' : 'secondary'}
                              >
                                {store.isVisible
                                  ? t('itemMaster.visible')
                                  : t('itemMaster.hidden')}
                              </Badge>
                              <Badge
                                variant={
                                  store.isAvailable ? 'success' : 'warning'
                                }
                              >
                                {store.isAvailable
                                  ? t('itemMaster.available')
                                  : t('itemMaster.unavailable')}
                              </Badge>
                              {store.sectionName && (
                                <span className="text-muted-foreground text-xs">
                                  {store.sectionName}
                                </span>
                              )}
                              {store.aisle && (
                                <span className="text-muted-foreground text-xs">
                                  {t('itemMaster.aisle')} {store.aisle}
                                </span>
                              )}
                              {store.quantityOnHand != null && (
                                <span className="text-muted-foreground text-xs tabular-nums">
                                  {formatNumber(store.quantityOnHand)}{' '}
                                  {t('itemMaster.onHand')}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={saving || !draft.name?.trim()} onClick={save}>
              {saving && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('itemMaster.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { itemLabel };
